import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Form } from 'react-bootstrap';
import { Controller } from 'react-hook-form';
import { InputValidation } from './components/InputValidation';
import { useInputComponent } from './hooks/useInputComponent';
import { transformValidationRules } from './utils';
import { NUMERIC_FIELD_TYPES } from './constants';
/**
 * `Input` is a versatile form input component designed for use in React applications. It integrates
 * seamlessly with `react-hook-form` for efficient form state management and validation,
 * while also providing a way to use Input and it's corresponding logic outside 'react-hook-form'.
 * It is capable of rendering various types of inputs like text, radio, checkbox, dropdown, textarea, and range,
 * based on the configuration passed through props. It simplifies the process of form creation by handling
 * different input types with a uniform interface.
 *
 * Props:
 * @param {CustomField<FieldValues>} field - The field configuration object, defined by the `CustomField`
 * @param {Control<FieldValues>} control - A `Control` object from `react-hook-form`, used for controlling
 * the input within the form.
 * @param {Path<FieldValues>} name - The name of the input field, corresponding to its key in form data.
 * @param {UseFormSetValue<FieldValues>} setValue - A `setValue` method from `react-hook-form`, utilized
 * for updating form values.
 *
 * The `Input` component uses the `useInputComponent` hook to determine the appropriate input
 * type to render based on the `field.type`. It also utilizes `useCustomValidation` for custom validation logic
 * and `transformValidationRules` to process validation rules. The `InputValidation` component
 * is used to display validation feedback.
 *
 * The component encapsulates complex form logic, offering a streamlined approach to handle different input
 * types and their validation, thereby enhancing the maintainability and scalability of form implementations
 * in React applications.
 */
export const Input = ({ control, definition, setValue, name, onChange, value, validationTrigger }) => {
    const InputComponent = useInputComponent(definition, setValue);
    return (_jsx(Form.Group, Object.assign({ className: 'pb-3' }, { children: _jsx(Controller, { defaultValue: '', name: name || definition.name, control: control, rules: Object.assign({}, transformValidationRules(definition.validation)), render: ({ field: inputField, fieldState: { invalid, error } }) => (_jsxs(_Fragment, { children: [_jsx(InputComponent, Object.assign({ label: definition.label, type: definition.fieldType }, inputField, { onChange: (e) => {
                            if (NUMERIC_FIELD_TYPES.includes(definition.fieldType)) {
                                const output = parseFloat(e.target.value);
                                return inputField.onChange(isNaN(output) ? '' : output);
                            }
                            return inputField.onChange(e);
                        }, isInvalid: invalid }), definition.id), _jsx(InputValidation, { error: error, validation: definition.validation })] })) }, `${definition.name}-${definition.id}`) })));
};
